import { COLORS } from '../../constants/theme';
import { DossierHeader } from './DossierHeader';
import { ResourcesTab } from './ResourcesTab';
import { EffectsTab } from './EffectsTab';
import { MilitaryTab } from './MilitaryTab';
import type { DesktopDossierShellProps, TabId } from '../../pages/Dossier';

const TAB_LABELS: Record<TabId, string> = {
  resources: 'Resources',
  effects: 'Effects',
  military: 'Military / TM',
};

/**
 * SF-156 Component 3: thin wrapper moving `Dossier.tsx`'s existing tab bar +
 * `ResourcesTab`/`EffectsTab`/`MilitaryTab`/`DossierHeader` JSX in verbatim. No behavior change on
 * desktop in this spec — a pure move, not a new decision (see Component 3's "Empty-state
 * ownership" note: this owns the entire `{dossier && ...}` block, not just the tab bar/panels).
 */
export function DesktopDossierShell({
  dossier,
  currentTick,
  activeTab,
  onTabChange,
  resourcesSort,
  effectsSort,
  militarySort,
  onResourcesSort,
  onEffectsSort,
  onMilitarySort,
}: DesktopDossierShellProps) {
  return (
    <>
      {/* Kingdom header */}
      <DossierHeader
        dossier={dossier}
        relationships={dossier.atWarWith.map((id) => ({
          kingdomId: id,
          kingdomName: null,
          relationship: 'WAR' as const,
          // KingdomDossierDto.atWarWith carries no ceasefire data — DossierHeader only reads
          // kingdomId/kingdomName/relationship (see its war-chips filter), so this is a safe
          // placeholder, not a claim about real ceasefire state.
          inCeasefire: false,
        }))}
      />

      {/* Empty state */}
      {dossier.provinces.length === 0 && (
        <div style={{ padding: 32, color: COLORS.text.muted, textAlign: 'center' }}>
          No members found for this kingdom.
        </div>
      )}

      {dossier.provinces.length > 0 && (
        <>
          {/* Tab bar */}
          <div
            role="tablist"
            className="flex border-b px-4"
            style={{ borderColor: COLORS.border, backgroundColor: COLORS.sectionHeader }}
          >
            {(['resources', 'effects', 'military'] as const).map((tab) => (
              <button
                key={tab}
                role="tab"
                id={`tab-${tab}`}
                aria-selected={activeTab === tab}
                aria-controls={`panel-${tab}`}
                onClick={() => onTabChange(tab)}
                className="py-3 px-4 text-sm font-medium mr-2"
                style={{
                  color: activeTab === tab ? COLORS.text.primary : COLORS.text.muted,
                  borderBottom: activeTab === tab
                    ? `2px solid ${COLORS.text.primary}`
                    : '2px solid transparent',
                  background: 'none',
                  cursor: 'pointer',
                }}
              >
                {TAB_LABELS[tab]}
              </button>
            ))}
          </div>

          {/* Tab panels — conditional rendering keeps sort state */}
          <div role="tabpanel" id="panel-resources" aria-labelledby="tab-resources">
            {activeTab === 'resources' && (
              <ResourcesTab
                provinces={dossier.provinces}
                currentTick={currentTick}
                sortState={resourcesSort}
                onSort={onResourcesSort}
              />
            )}
          </div>
          <div role="tabpanel" id="panel-effects" aria-labelledby="tab-effects">
            {activeTab === 'effects' && (
              <EffectsTab
                provinces={dossier.provinces}
                currentTick={currentTick}
                sortState={effectsSort}
                onSort={onEffectsSort}
              />
            )}
          </div>
          <div role="tabpanel" id="panel-military" aria-labelledby="tab-military">
            {activeTab === 'military' && (
              <MilitaryTab
                provinces={dossier.provinces}
                currentTick={currentTick}
                sortState={militarySort}
                onSort={onMilitarySort}
              />
            )}
          </div>
        </>
      )}
    </>
  );
}
